import { ChevronRight, Close, DoNotDisturbOnOutlined, LanguageOutlined } from '@mui/icons-material';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Link, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

const DEFAULT_CLOSING_MESSAGE = (agencyName: string) =>
    `Votre agence ${agencyName} a fermé ses portes. Retrouvez toutes nos agences sur notre site.`;

const MAX_MESSAGE_LENGTH = 500;

// Chronos symbol (same paths as the mobile app's logo.svg), colored like the badge in the app.
const ChronosMark = () => (
    <svg width='22' height='18' viewBox='0 0 56 45' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
        <path d='M22.8176 31.2169V19.6853C22.8176 16.9885 23.4936 14.3594 24.7459 12.0271H19.3697C15.0257 12.0271 10.8547 13.7451 7.78402 16.7959L0.95106 23.5795C-0.31702 24.8394 -0.31702 26.8802 0.95106 28.14L16.9697 44.0551C18.2378 45.315 20.2919 45.315 21.56 44.0551L28.3877 37.2716C29.5405 36.1262 30.5047 34.8247 31.2592 33.419H25.0289C23.808 33.419 22.8176 32.4351 22.8176 31.2221V31.2169Z' fill='#FF8D3A' />
        <path d='M55.0543 17.3009L39.0355 1.38582C37.7674 0.125949 35.7133 0.125949 34.4453 1.38582L27.6175 8.16937C26.4647 9.31471 25.5005 10.6162 24.7459 12.0219H30.9764C32.1973 12.0219 33.1876 13.0058 33.1876 14.2189V25.7504C33.1876 28.4471 32.5117 31.0762 31.2593 33.4085H36.6356C40.9796 33.4085 45.1507 31.6957 48.2213 28.6397L55.0491 21.8562C56.3172 20.5963 56.3172 18.5555 55.0491 17.2957L55.0543 17.3009Z' fill='#FF8D3A' />
    </svg>
);
const CHRONOS_WEBSITE_URL = 'https://www.chronos.jobs';

type CloseAgencyDialogProps = {
    open: boolean;
    agencyName: string;
    tempWorkersCount: number;
    loading?: boolean;
    onClose: () => void;
    onConfirm: (message: string) => void;
};

const CloseAgencyDialog = ({ open, agencyName, tempWorkersCount, loading = false, onClose, onConfirm }: CloseAgencyDialogProps) => {
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (!open) { setMessage(''); }
    }, [open]);

    const trimmedMessage = message.trim();
    const previewMessage = trimmedMessage || DEFAULT_CLOSING_MESSAGE(agencyName);

    return (
        <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth='sm'>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Stack direction='row' spacing={1} alignItems='center'>
                    <DoNotDisturbOnOutlined color='error' />
                    <Typography variant='h6'>Fermer l'agence {agencyName}</Typography>
                </Stack>
                <IconButton onClick={onClose} disabled={loading}>
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
                    Une fois fermée :
                </Typography>
                <Box component='ul' sx={{ m: 0, mb: 2, pl: 2.5, color: 'text.secondary', fontSize: '0.875rem', '& li': { mb: 0.5 } }}>
                    <li>l'agence disparaît pour les managers, associés et directeurs — seuls les administrateurs la voient encore, avec le tag « Fermée » ;</li>
                    <li>les <strong>{tempWorkersCount} intérimaire{tempWorkersCount > 1 ? 's' : ''}</strong> rattaché{tempWorkersCount > 1 ? 's' : ''} restent rattaché{tempWorkersCount > 1 ? 's' : ''} à l'agence et verront votre message dans l'application ;</li>
                    <li>les nouveaux inscrits seront rattachés à l'agence ouverte la plus proche de chez eux.</li>
                </Box>
                <TextField
                    label='Message affiché aux intérimaires'
                    fullWidth
                    multiline
                    minRows={4}
                    value={message}
                    onChange={(event) => setMessage(event.target.value.slice(0, MAX_MESSAGE_LENGTH))}
                    helperText={`${message.length}/${MAX_MESSAGE_LENGTH} — laissé vide, le message par défaut ci-dessous sera affiché.`}
                    placeholder="Ex : Notre agence de Moulins ferme ses portes le 30 septembre. Votre dossier reste actif : l'agence de Clermont-Ferrand (04 73 00 00 00) prend le relais pour vos prochaines missions."
                    disabled={loading}
                />

                <Typography sx={{ mt: 2, mb: 0.75, fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' }}>
                    Aperçu dans l'application
                </Typography>
                <Box sx={{ maxWidth: 360, borderRadius: '16px', bgcolor: '#FFFFFF', boxShadow: '0 4px 8px rgba(0,0,0,0.08)', border: '1px solid #EDF0F3', overflow: 'hidden' }}>
                    <Box sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: '#FFF1E6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <ChronosMark />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0, minHeight: 40, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#FF8D3A', mb: 0.25 }}>
                                    Agence fermée
                                </Typography>
                                <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#15161A', lineHeight: 1.3 }}>
                                    {agencyName}
                                </Typography>
                            </Box>
                        </Box>
                        <Typography sx={{ mt: 1.5, fontSize: '0.875rem', lineHeight: '20px', color: '#666D78', whiteSpace: 'pre-wrap' }}>
                            {previewMessage}
                        </Typography>
                    </Box>
                    <Box sx={{ mx: 2, borderTop: '1px solid #EDF0F3' }} />
                    <Link href={CHRONOS_WEBSITE_URL} target='_blank' rel='noreferrer' underline='none' sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5, fontSize: '0.875rem', color: '#3E434A' }}>
                        <LanguageOutlined sx={{ fontSize: '1.125rem', color: '#3E434A' }} />
                        <Box component='span' sx={{ flex: 1 }}>www.chronos.jobs</Box>
                        <ChevronRight sx={{ fontSize: '1.125rem', color: '#88919E' }} />
                    </Link>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button variant='outlined' color='inherit' onClick={onClose} disabled={loading} sx={{ color: 'text.secondary', borderColor: '#EDF0F3', '&:hover': { borderColor: '#D6DBE1', bgcolor: 'background.default' } }}>Annuler</Button>
                <Button variant='contained' color='error' onClick={() => onConfirm(trimmedMessage)} disabled={loading}>
                    {loading && <CircularProgress size={12} sx={{ mr: 1 }} />}
                    Fermer l'agence
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { CloseAgencyDialog, DEFAULT_CLOSING_MESSAGE };
